import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import {
  acceptAllChoice,
  customChoice,
  readConsent,
  rejectAllChoice,
  writeConsent,
  type ConsentChoice,
} from "@/lib/cookie-consent";
import { isHydratingPrerender } from "@/lib/hydration";

interface CookieConsentValue {
  /** Decisión actual o null si aún no se ha consentido. */
  choice: ConsentChoice | null;
  /** ¿Hay que mostrar el banner inicial? (sólo true si choice === null) */
  needsDecision: boolean;
  /** ¿El panel de personalización está abierto? Útil para el footer link. */
  customizing: boolean;
  openCustomizer: () => void;
  closeCustomizer: () => void;
  acceptAll: () => void;
  rejectAll: () => void;
  saveCustom: (opts: { analytics: boolean; marketing: boolean }) => void;
}

const CookieConsentContext = createContext<CookieConsentValue | null>(null);

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  // LDT-718 · Al hidratar la landing, el primer render tiene que coincidir con
  // el HTML prerenderizado, que se generó sin consentimiento guardado (banner
  // visible): se empieza en `null` y el efecto de abajo lee el de verdad. Es el
  // mismo destello del banner que ya había (PROB-782), ahora sin tirar la
  // página. En el resto de arranques se lee al momento, como siempre.
  const [choice, setChoice] = useState<ConsentChoice | null>(() =>
    isHydratingPrerender() ? null : readConsent(),
  );
  const [customizing, setCustomizing] = useState(false);

  // Y en una transición, no como actualización normal: si el trozo de la
  // página (las spokes van en lazy) aún no ha llegado, su Suspense sigue
  // deshidratado, y un cambio urgente del contexto obligaría a React a tirarlo
  // y pintarlo desde cero con su fallback vacío: la página en blanco unos
  // cientos de ms (error #421, QA 2 de LDT-718). Como transición, React espera
  // a hidratarla y aplica el cambio después.
  useEffect(() => {
    if (isHydratingPrerender()) startTransition(() => setChoice(readConsent()));
  }, []);

  useEffect(() => {
    // Si otro tab cambia el consentimiento, sincronizamos.
    const handler = (e: StorageEvent) => {
      if (e.key === "playium_cookie_consent_v1") {
        setChoice(readConsent());
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  const acceptAll = useCallback(() => {
    const c = acceptAllChoice();
    writeConsent(c);
    setChoice(c);
    setCustomizing(false);
  }, []);

  const rejectAll = useCallback(() => {
    const c = rejectAllChoice();
    writeConsent(c);
    setChoice(c);
    setCustomizing(false);
  }, []);

  const saveCustom = useCallback((opts: { analytics: boolean; marketing: boolean }) => {
    const c = customChoice(opts);
    writeConsent(c);
    setChoice(c);
    setCustomizing(false);
  }, []);

  const value = useMemo<CookieConsentValue>(
    () => ({
      choice,
      needsDecision: choice === null,
      customizing,
      openCustomizer: () => setCustomizing(true),
      closeCustomizer: () => setCustomizing(false),
      acceptAll,
      rejectAll,
      saveCustom,
    }),
    [choice, customizing, acceptAll, rejectAll, saveCustom],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook del contexto; separarlo de CookieConsentProvider obligaría a tocar todos sus consumidores
export function useCookieConsent(): CookieConsentValue {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) throw new Error("useCookieConsent must be used inside <CookieConsentProvider>");
  return ctx;
}
